'use client';

import {
  createContext,
  useContext,
  useCallback,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type MouseEvent,
} from 'react';

export interface AccordionItemType {
  question: ReactNode;
  answer: ReactNode;
  note?: ReactNode;
}

interface AccordionContextValue {
  openIndex: number;
  handleToggle: (idx: number) => void;
  getButtonId: (index: number) => string;
  getPanelId: (index: number) => string;
}

interface AccordionProps {
  index?: number;
  children: ReactNode;
}

interface AccordionButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onToggle'> {
  index: number;
}

interface AccordionPanelProps extends HTMLAttributes<HTMLDivElement> {
  index: number;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

export function useAccordion() {
  const context = useContext(AccordionContext);
  if (!context) throw new Error('Accordion components must be used inside <Accordion>.');
  return context;
}

export default function Accordion({ index = 0, children }: AccordionProps) {
  const [openIndex, setOpenIndex] = useState(index);
  const accordionId = useId();

  const handleToggle = useCallback((idx: number) => {
    // 이미 열린 항목을 다시 누르면 -1 → 전부 닫힘 (한 번에 하나만 열리는 단일 오픈)
    setOpenIndex((prev) => (prev === idx ? -1 : idx));
  }, []);

  // 버튼·패널이 같은 규칙으로 id를 만들어 ARIA(aria-controls/labelledby)로 서로를 가리키게 함
  const getButtonId = useCallback((i: number) => `${accordionId}-button-${i}`, [accordionId]);
  const getPanelId = useCallback((i: number) => `${accordionId}-panel-${i}`, [accordionId]);

  return (
    <AccordionContext.Provider
      value={{
        openIndex,
        handleToggle,
        getButtonId,
        getPanelId,
      }}
    >
      {children}
    </AccordionContext.Provider>
  );
}

export function AccordionButton({
  index,
  type = 'button',
  onClick,
  ...rest
}: AccordionButtonProps) {
  const { openIndex, handleToggle, getButtonId, getPanelId } = useAccordion();
  const isOpen = openIndex === index;
  const buttonId = getButtonId(index);
  const panelId = getPanelId(index);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented) handleToggle(index);
  };

  return (
    <button
      {...rest}
      type={type}
      id={buttonId}
      aria-expanded={isOpen}
      aria-controls={panelId}
      onClick={handleClick}
    />
  );
}

export function AccordionPanel({ index, ...rest }: AccordionPanelProps) {
  const { openIndex, getButtonId, getPanelId } = useAccordion();
  const isOpen = openIndex === index;
  const buttonId = getButtonId(index);
  const panelId = getPanelId(index);

  return (
    <div
      {...rest}
      id={panelId}
      role="region"
      aria-labelledby={buttonId}
      inert={!isOpen ? true : undefined}
    />
  );
}
